import { FORM_ERROR } from 'final-form';
import { Component } from 'react';
import { Col, Row } from 'antd';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { WithTranslation, withTranslation } from 'react-i18next';

import { getCompanyContentLocale } from '~src/services/session';
import { PageContentContainer } from '~src/styles/containers.sc';
import AddItemPageHeader from '~src/shared/headers/AddItemPageHeader';
import { addTopic, TopicCreate } from '~src/modules/topics/actions/topicActions';
import logger from '~src/services/logger/LlsLogger';
import { Scale, ScalesRespI, FetchScalesParams } from '~src/interfaces/scale.interface';
import { fetchScales } from '~src/modules/scales/actions/scaleActions';
import { TopicSubject } from '~src/interfaces/topic.interface';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import { StoreI } from '~src/rootReducer';

import TopicAddForm from './TopicAddForm';

interface DispatchProps {
  addTopic(topic: TopicCreate): ApiReduxResp;
  fetchScales(props?: FetchScalesParams): ApiReduxResp<ScalesRespI>;
}

interface StateProps {
  contentLocale: string;
  scales: { [key: string]: Scale };
}

interface DefaultProps {
  scales: { [key: string]: Scale };
}

type Props = DispatchProps & RouteComponentProps<any> & WithTranslation & StateProps;

export interface FormValues {
  name: string;
  subject: TopicSubject;
  comments?: string;
}

class TopicAdd extends Component<Props> {
  static defaultProps: DefaultProps = {
    scales: {},
  };

  onSubmit = async (topic: FormValues) => {
    // needed on create Topic these are defaults
    if (Object.keys(this.props.scales).length < 1) {
      await this.props.fetchScales({ per_page: 1000 });
    }
    const tmpTopic: TopicCreate = {
      comments: 'optional',
      name: { [this.props.contentLocale]: topic.name.trim() },
      scaleId: Object.keys(this.props.scales)[0],
      subject: topic.subject,
    };
    return this.props
      .addTopic(tmpTopic)
      .then(response => {
        if (response.error) {
          const { error } = response;
          return {
            name: error.response ? error.response.data[0].translation_key : 'UNKNOWN',
            [FORM_ERROR]: 'add topic failed!',
          };
        }
        this.props.history.push(`/content/topics/${response.response ? response.response.data.id : ''}`);
      })
      .catch(error => {
        logger.info('Error creating Topic', {
          error,
          info: {
            componentStack: 'TopicAdd.jsx',
          },
        });
        throw error;
      });
  };

  render() {
    const { t } = this.props;
    return (
      <PageContentContainer>
        <Row gutter={30}>
          <Col span={12} offset={6}>
            <AddItemPageHeader
              t={t}
              titleKey="x.topics.add.topic.title"
              subTitleKey="x.topics.add.topic.description"
            />
          </Col>
        </Row>
        <Row>
          <Col lg={24}>
            <TopicAddForm
              history={this.props.history}
              onFrmSubmit={this.onSubmit}
              contentLocale={this.props.contentLocale}
            />
          </Col>
        </Row>
      </PageContentContainer>
    );
  }
}

const mapStateToProps = (state: StoreI): StateProps => ({
  contentLocale: getCompanyContentLocale(state),
  scales: state.scales.byId,
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  addTopic: (topic: TopicCreate) => dispatch(addTopic(topic)),
  fetchScales: params => dispatch(fetchScales(params)),
});

// @see https://github.com/gaearon/react-hot-loader/blob/master/docs/Troubleshooting.md#react-hot-loader-this-component-is-not-accepted-by-hot-loader
// we need to wrap each wrapped component to get the hot loader working properly
const TopicAddR = withRouter(TopicAdd);
const TopicAddT = withTranslation()(TopicAddR);
const TopicAddSuperComponent = connect(mapStateToProps, mapDispatchToProps)(TopicAddT);

export default TopicAddSuperComponent;
